/**
 * Cookie utility with security best practices
 * Default expiration: 90 days (for authentication cookies)
 *
 * IMPORTANT: On localhost, cookies are shared across ports (3000 admin / 3001 user).
 * Use app-specific token cookie names so the two apps never overwrite each other.
 *
 * Sanctum tokens contain "|". Always encode cookie values. Also keep a
 * sessionStorage + in-memory copy so admin→user switch still has a Bearer
 * token even if the cookie write is flaky.
 */

export const USER_AUTH_TOKEN_COOKIE = 'user_auth_token';
export const LEGACY_AUTH_TOKEN_COOKIE = 'auth_token';
const USER_AUTH_TOKEN_STORAGE_KEY = 'user_auth_token';

/** In-memory fallback for the current tab (set on login / switch bootstrap). */
let memoryUserAuthToken = null;

/**
 * Set a cookie with 90-day expiration by default
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 90)
 */
export const setCookie = (name, value, days = 90) => {
    if (value === undefined || value === null) return;

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    // Encode so Sanctum tokens (contain "|") and other special chars stay intact
    const encodedValue = encodeURIComponent(String(value));
    const isSecure = window.location.protocol === 'https:';

    // First-party app cookies: Lax is enough and more reliable than SameSite=None
    const cookieString = isSecure
        ? `${name}=${encodedValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`
        : `${name}=${encodedValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
            const raw = c.substring(nameEQ.length, c.length);
            try {
                return decodeURIComponent(raw);
            } catch {
                return raw;
            }
        }
    }
    return null;
};

/**
 * Remove/delete a cookie
 * @param {string} name - Cookie name
 */
export const removeCookie = (name) => {
    const isSecure = window.location.protocol === 'https:';
    // Clear with the same path/SameSite variants we may have written historically
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
    if (isSecure) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure`;
    }
};

const readStoredUserAuthToken = () => {
    try {
        return sessionStorage.getItem(USER_AUTH_TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
};

const writeStoredUserAuthToken = (token) => {
    try {
        if (token) {
            sessionStorage.setItem(USER_AUTH_TOKEN_STORAGE_KEY, token);
        } else {
            sessionStorage.removeItem(USER_AUTH_TOKEN_STORAGE_KEY);
        }
    } catch {
        // private mode / blocked storage — memory + cookie still apply
    }
};

export const getUserAuthToken = () =>
    memoryUserAuthToken ||
    getCookie(USER_AUTH_TOKEN_COOKIE) ||
    getCookie(LEGACY_AUTH_TOKEN_COOKIE) ||
    readStoredUserAuthToken();

export const setUserAuthToken = (token, days = 90) => {
    if (!token) return;
    memoryUserAuthToken = String(token);
    writeStoredUserAuthToken(memoryUserAuthToken);
    setCookie(USER_AUTH_TOKEN_COOKIE, memoryUserAuthToken, days);
    // Do not keep writing the shared legacy name — it collides with admin on localhost
    removeCookie(LEGACY_AUTH_TOKEN_COOKIE);
};

export const clearUserAuthToken = () => {
    memoryUserAuthToken = null;
    writeStoredUserAuthToken(null);
    removeCookie(USER_AUTH_TOKEN_COOKIE);
    removeCookie(LEGACY_AUTH_TOKEN_COOKIE);
};

/**
 * Helper to get all cookies as object
 * @returns {object} - Object with all cookies
 */
export const getAllCookies = () => {
    const cookies = {};
    document.cookie.split(';').forEach((cookie) => {
        const [name, ...rest] = cookie.trim().split('=');
        if (name) {
            const raw = rest.join('=');
            try {
                cookies[name] = decodeURIComponent(raw);
            } catch {
                cookies[name] = raw;
            }
        }
    });
    return cookies;
};

/**
 * Check if user is authenticated (has user_id cookie)
 * @returns {boolean} - True if authenticated
 */
export const isAuthenticated = () => {
    return !!getCookie('user_id');
};

/**
 * Get user ID from cookie
 * @returns {string|null} - User ID or null
 */
export const getUserId = () => {
    return getCookie('user_id');
};

/**
 * Get company ID from cookie
 * @returns {string|null} - Company ID or null
 */
export const getCompanyId = () => {
    return getCookie('company_id');
};

/**
 * Clear only USER authentication cookies.
 * Never touch admin_id / admin_auth_token (shared localhost cookie jar).
 */
export const clearAuthCookies = () => {
    removeCookie('user_id');
    removeCookie('username');
    clearUserAuthToken();
    // Keep company_id — shared with admin on localhost; wiping it logs admin out too
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
};

/**
 * Bootstrap auth cookies from admin→user switch URL params.
 * Must run synchronously before protected routes / child pages redirect.
 * @param {string} [search=window.location.search]
 * @returns {{ didBootstrap: boolean, userId: string|null, companyId: string|null, token: string|null }}
 */
export const bootstrapSwitchAuthFromUrl = (search = window.location.search) => {
    const urlParams = new URLSearchParams(search);
    const userId = urlParams.get('user_id');
    const companyId = urlParams.get('company_id');
    // URLSearchParams already decodes; keep a safe fallback for double-encoded values
    let token = urlParams.get('token');
    if (token) {
        try {
            if (token.includes('%')) {
                token = decodeURIComponent(token);
            }
        } catch {
            // keep raw token
        }
    }

    if (!userId) {
        return { didBootstrap: false, userId: null, companyId: null, token: null };
    }

    setCookie('user_id', userId, 90);
    if (companyId && companyId !== 'undefined' && companyId !== 'null') {
        setCookie('company_id', companyId, 90);
    }
    if (token) {
        setUserAuthToken(token, 90);
    }

    return { didBootstrap: true, userId, companyId, token };
};
